/* © CNES */
import { useQuery } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteSavedProduct } from '../../../../api/cart/Cart';
import { downloadProductZipFile } from '../../../../api/items/Items';
import { getDownloadQuotas } from '../../../../api/process/process';
import { ConfigContext } from '../../../../context/ConfigContext';
import {
  AssetWithProductId,
  CollectionDetails,
} from '../../../../models/CollectionDetails';
import { ProductsAdded } from '../../../../models/ProductsAdded';
import { ShowFilesToDownloadDialog } from '../../../explore/RightSidePanel/CollectionActionButtons/ShowFilesToDownloadDialog/ShowFilesToDownloadDialog';
import { DATASET } from '../../../shared/Constants';
import { isItemAvailable } from '../../../shared/Utils';
import './AddedToolbar.scss';

/**
 * Added Toolbar Props.
 */
interface AddedToolbarProps {
  productsAdded: ProductsAdded;
  selectedItems: string[];
  setProductsAdded: Function;
  setSelectedItems: Function;
  handleVisibleDialog: () => void;
  setDownloadProgress: Function;
  itemsAvailabilitySummary: {
    productId: string;
    available: boolean | undefined;
  }[];
}

/**
 * AddedToolbar displays user actions for Added section in myData.
 * @param productsAdded The products added by user.
 * @param selectedItems The products selected by user.
 * @param setProductsAdded Function to set productsAdded.
 * @param setSelectedItems Function to set selected items.
 * @param handleVisibleDialog Function to handle Dialog Visibility.
 * @param setDownloadProgress Function setDownloadProgress.
 * @param itemsAvailabilitySummary summary of items availibility from datalake.
 * @returns Element.
 */
export const AddedToolbar = ({
  productsAdded,
  selectedItems,
  setProductsAdded,
  setSelectedItems,
  handleVisibleDialog,
  setDownloadProgress,
  itemsAvailabilitySummary,
}: AddedToolbarProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [assetsToShow, setAssetsToShow] = useState<AssetWithProductId[]>([]);
  const [assetsToDownload, setAssetsToDownload] = useState<
    AssetWithProductId[]
  >([]);
  const context = useContext(ConfigContext);
  const navigate = useNavigate();
  /**
   * remove a product from productsAdded.
   */
  const remove = () => {
    if (productsAdded) {
      setProductsAdded({
        products: productsAdded?.products?.filter(
          (el) => !selectedItems.includes(el)
        ),
        results: productsAdded.results.filter(
          (el) => !selectedItems.includes(el)
        ),
        levelSet: productsAdded.levelSet,
        satelliteSet: productsAdded.satelliteSet,
        collectionsDetails: productsAdded.collectionsDetails.filter(
          (el) => !selectedItems.includes(el.productName)
        ),
      } as ProductsAdded);
    }
    setSelectedItems([]);
  };

  /**
   * UseQuery synthax to call deleteSavedProduct API to remove products from Cart
   * @requires products name are required to delete them
   * @param user
   * @param products
   */
  const { refetch } = useQuery({
    queryKey: [
      'DeleteSavedProduct',
      {
        payload: selectedItems.map((el) => {
          return {
            user: context.userLoggedIn.email,
            name: el,
            downloaded: context.productsAdded.collectionsDetails?.find(
              (collection) => collection.productName === el
            )?.checksum,
            mission: context.productsAdded.collectionsDetails?.find(
              (collection) => collection.productName === el
            )?.collectionDataModel?.productDetails[DATASET],
            urn: context.productsAdded.collectionsDetails?.find(
              (collection) => collection.productName === el
            )?.id,
            quicklook: context.productsAdded.collectionsDetails?.find(
              (collection) => collection.productName === el
            )?.quicklook,
          };
        }),
      },
    ],
    queryFn: deleteSavedProduct,
    enabled: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });

  /**
   * accept to remove a product.
   */
  const accept = () => {
    void refetch().then((res) => {
      if (res.isSuccess) {
        remove();
      }
    });
  };
  /**
   * reject to remove a product.
   */
  const reject = () => {};
  /**
   * Confirm to remove a product by dialog.
   */
  const confirm = () => {
    confirmDialog({
      message: 'Do you want to remove selected products from My Data ?',
      header: 'Remove selected products',
      acceptClassName: 'BtnAccept',
      rejectClassName: 'BtnReject',
      accept,
      reject,
      acceptLabel: 'Yes, Please',
      rejectLabel: 'Nope!',
      className: 'ConfirmDialogRemove',
    });
  };

  const { refetch: refetchQuotasDownload } = useQuery({
    queryKey: ['QuotasDownload', navigate, context.setError400],
    queryFn: getDownloadQuotas,
    enabled: false, // This prevents the query from automatically running
  });

  /**
   * prepare exclusion list of extentions
   * @returns exclusion list of extentions
   */
  const prepareExclusionList = () => {
    // Application get list of exclusion files type from runtime var.
    const assetsFileTypeExclusionString =
      process.env.NODE_ENV === 'production'
        ? window.__RUNTIME_CONFIG__?.ASSETS_FILE_TYPE_EXCLUSION
        : 'application/xml,application/json';
    // transform into list object without spacing.
    return assetsFileTypeExclusionString.replace(' ', '').split(',');
  };

  /**
   * prepare Download Mode
   * @returns isSingleFiledownloadMode boolean
   */
  const prepareDownloadModeForMultipleProducts = (
    selectedProducts: CollectionDetails[]
  ) => {
    const allIsAssetsUnique = selectedProducts.map((selectedProduct) => {
      let isSingleFiledownloadMode = true;
      const assetsFileTypeExclusionList = prepareExclusionList();

      const dataRoleAssetsList = selectedProduct?.assets?.filter(
        (asset) =>
          asset.roles.includes('data') &&
          assetsFileTypeExclusionList.every(
            (typeExclusion) => asset.type !== typeExclusion
          )
      );

      if (dataRoleAssetsList?.length > 1) {
        isSingleFiledownloadMode = false;
      }
      return isSingleFiledownloadMode;
    });
    return allIsAssetsUnique.every((assetUnique) => assetUnique === true);
  };

  /**
   * prepare Multiple Assets To Download when multiple products are selected.
   * @returns assets With No Exclusion Types list
   */
  const prepareMultipleAssetsToDownload = (
    selectedProducts: CollectionDetails[]
  ) => {
    const assetsFileTypeExclusionList = prepareExclusionList();

    const assetsWithNoExclusionTypes = selectedProducts
      .map((selectedProduct) => {
        return selectedProduct.assets
          .filter(
            (element) => !assetsFileTypeExclusionList.includes(element.type)
          )
          .map((asset) => {
            return {
              href: asset.href,
              title: asset.title,
              description: asset.description,
              type: asset.type,
              roles: asset.roles,
              productId: selectedProduct.id,
            } as AssetWithProductId;
          });
      })
      .flat();

    return assetsWithNoExclusionTypes;
  };

  /**
   * Launch downloads on multiple assets when user select them is Dialog.
   */
  useEffect(() => {
    if (assetsToDownload.length) {
      // Download assets selected
      Promise.all(
        assetsToDownload.map((asset) =>
          downloadProductZipFile(
            asset.productId,
            asset.href.split('files/')[1],
            asset.title,
            context.setError400,
            setDownloadProgress,
            navigate,
            true
          )
        )
      ).finally(() => {
        context.setDownloadingProducts(context.selectedProduct?.id);
        setIsLoading(false);
      });
    }
    // eslint-disable-next-line
  }, [assetsToDownload]);

  /**
   * Handles the download process for the selected products.
   * @param selectedProducts An array of selected product details to be downloaded.
   */
  const processProductDownloads = (selectedProducts: CollectionDetails[]) => {
    const isSingleFiledownloadMode =
      prepareDownloadModeForMultipleProducts(selectedProducts);
    // all products are archived
    if (isSingleFiledownloadMode) {
      setIsLoading(true);
      Promise.all(
        // throttle downloads to 3 requests per second.
        selectedProducts.map(async (product) => {
          context.setDownloadingProducts(product.id);
          try {
            // Ajout du log pour debug
            const safeAssetTitle =
              product.assets && product.assets.length > 0
                ? product.assets[0].title
                : undefined;
            console.log('[AddedToolbar] Download debug:', {
              assetTitle: safeAssetTitle,
              productName: product.productName,
            });
            return await downloadProductZipFile(
              product.id,
              product.checksum,
              safeAssetTitle ?? product.productName,
              context.setError400,
              setDownloadProgress,
              navigate,
              false
            );
          } finally {
            context.setDownloadingProducts(product.id);
          }
        })
      ).finally(() => {
        setIsLoading(false);
      });
    } else {
      // At least one product is not archived and we use dialog to show user a download list
      // Get all assets that have role type and not in exclusion list to show user the dialog download choices.
      setAssetsToShow(prepareMultipleAssetsToDownload(selectedProducts));
      // Show Dialog to ask user to choose assets to download
      setShowDialog(true);
    }
  };

  /**
   * Download multiple Product files.
   */
  const downloadProducts = () => {
    let selectedProducts = productsAdded.collectionsDetails.filter((el) =>
      selectedItems.includes(el.productName)
    );

    const areAnyProductsUnavailable = selectedProducts.some(
      (el) => !isItemAvailable(itemsAvailabilitySummary, el.id)
    );

    if (areAnyProductsUnavailable) {
      selectedProducts = selectedProducts.filter(({ id }) =>
        isItemAvailable(itemsAvailabilitySummary, id)
      );
    }

    refetchQuotasDownload().then((quotasDownload) => {
      const remainingQuota = quotasDownload.data?.remainingQuota;
      const isQuotaExceeded =
        remainingQuota !== -1 &&
        Number(remainingQuota) < selectedProducts.length;

      const messageQuotas = (
        <React.Fragment>
          <p>
            Your request exceeds the amount of data you are allowed to download
            at once. Please reduce the scope of your download to a maximum of
            <span className="quotasDownload">{remainingQuota}</span> products or
            try again later. You can consult your detailed quota in My Profile.
          </p>
        </React.Fragment>
      );

      const showConfirmDialog = (
        message: React.ReactNode,
        header: string,
        headerColor: string,
        acceptCallback?: (() => void) | undefined
      ) => {
        confirmDialog({
          message,
          header,
          headerStyle: { color: headerColor },
          acceptClassName: 'BtnAccept',
          rejectClassName: 'RejectBtn',
          accept: acceptCallback,
          acceptLabel: 'Ok',
        });
      };

      if (isQuotaExceeded) {
        showConfirmDialog(messageQuotas, 'Download cannot proceed', 'red');
      } else if (areAnyProductsUnavailable) {
        const messageProductNotDownloaded = (
          <React.Fragment>
            <p>
              Products marked with a warning{' '}
              <i
                className="pi pi-exclamation-triangle"
                style={{ color: '#EF4A11', fontSize: '25px' }}
              />{' '}
              will not be downloaded. You can restore them using the Restore
              button{' '}
              <i
                className="pi pi-cloud-download"
                style={{ color: '#F59E0B', fontSize: '25px' }}
              />
              .
            </p>
          </React.Fragment>
        );
        showConfirmDialog(
          messageProductNotDownloaded,
          'Notice: Products Not Downloaded',
          '#EF4A11',
          () => processProductDownloads(selectedProducts)
        );
      } else {
        processProductDownloads(selectedProducts);
      }
    });
  };

  return (
    <div className="AddedToolbarContainer">
      <ShowFilesToDownloadDialog
        assetsFromProduct={assetsToShow}
        setAssetsToDownload={setAssetsToDownload}
        showDialog={showDialog}
        setShowDialog={setShowDialog}
      />
      <p className="DataInformation">{selectedItems.length} Data ready</p>
      <Button
        icon="pi pi-cog"
        className="DataAddedButton p-button-danger mr-2"
        label="Process"
        onClick={handleVisibleDialog}
        disabled={!selectedItems.length}
      />
      <Button
        icon="pi pi-arrow-circle-down"
        className="DataAddedButton p-button-warning mr-2"
        label="Download&nbsp;&nbsp;&nbsp;&nbsp;"
        disabled={!selectedItems.length}
        onClick={downloadProducts}
        loading={isLoading}
        tooltipOptions={{ position: 'top' }}
        tooltip="Download may take a few minutes to start"
      >
        {/* HIDDEN ({downloadTimeEstimation}min) */}
      </Button>
      {/* HIDDEN <Button
      icon={<img alt="python icon" src={require('./pythonicon.png')} />}
      className="DataAddedButton p-button-warning mr-2"
      label="&nbsp;&nbsp;Python Script"
    /> */}
      <ConfirmDialog />
      <Button
        className="DataAddedButton CustomOutlined"
        label="Remove"
        outlined
        onClick={confirm}
        disabled={!selectedItems.length}
      />
    </div>
  );
};
